/* Phase 3 — DPR final compliance pages */
import React, { useEffect, useState, useRef } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Check, CheckCircle, MessageCircle, Send, ExternalLink, Upload } from "lucide-react";
import { fmtINR, CANONICAL_STORES, storeLabel } from "../../lib/constants";
import Pagination, { usePagination, TableScroller } from "../../components/Pagination";
import DateInput from "../../components/DateInput";
import Paginated from "../../components/Paginated";
import { useAuth } from "../../lib/auth";

// ============ FEATURE TOGGLE MATRIX ============
export function FeatureToggleMatrixPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/api/crm/users").then((r) => setUsers(r.data || []));
    api.get("/api/crm/features/all").then((r) => setFeatures(r.data.features || []));
  }, []);

  const toggle = async (u, key) => {
    const cur = new Set(u.features || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    const arr = Array.from(cur);
    await api.patch(`/api/crm/users/${u.id}/features`, { features: arr });
    toast.success("Updated");
    setUsers(users.map((x) => x.id === u.id ? { ...x, features: arr } : x));
    if (active?.id === u.id) setActive({ ...u, features: arr });
  };

  const byModule = features.reduce((acc, f) => { (acc[f.module] = acc[f.module] || []).push(f); return acc; }, {});
  // Tasks the Super Admin can hand to a Major Admin / Admin
  const delegatable = features.filter((f) => f.delegatable);
  const isSuper = hasRole(me, "super_admin");

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="eyebrow">Permissions</div>
        <h1 className="font-display text-3xl text-maroon">Feature Toggles</h1>
        <p className="text-xs text-ash mt-1">Per-user, per-feature. Updates take effect on next page load — staff doesn't need to log out.</p>
        <div className="bg-white border border-line mt-4 max-h-[70vh] overflow-y-auto">
          {users.map((u) => (
            <button key={u.id} onClick={() => setActive(u)} className={`w-full text-left p-3 border-b border-line/60 hover:bg-pearl ${active?.id === u.id ? "bg-pearl" : ""}`} data-testid={`ft-user-${u.id}`}>
              <div className="font-medium text-sm">{u.name}</div>
              <div className="text-[10px] text-ash">{u.role} · {(u.features || []).length} features</div>
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2">
        {!active ? (
          <div className="bg-white border border-line p-12 text-center text-ash">Select a user.</div>
        ) : (
          <div>
            <div className="font-display text-2xl text-maroon">{active.name} · <span className="text-base text-ash">{active.role}</span></div>

            {/* Super Admin delegation — tasks that normally only Super Admin can do */}
            {isSuper && active.role !== "super_admin" && delegatable.length > 0 && (
              <div className="bg-white border-2 border-gold p-5 mt-4" data-testid="delegation-panel">
                <div className="eyebrow mb-1 text-maroon">Delegated by Super Admin</div>
                <p className="text-xs text-ash mb-3">
                  These are Super Admin tasks. Tick one to let {active.name.split(" ")[0]} handle it —
                  they'll see it in their own dashboard. Untick to take it back.
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {delegatable.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`delegate-${active.id}-${f.key}`}>
                      <input
                        type="checkbox"
                        checked={(active.features || []).includes(f.key) || (active.features || []).includes("*")}
                        onChange={() => toggle(active, f.key)}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {Object.entries(byModule).map(([mod, items]) => (
              <div key={mod} className="bg-white border border-line p-5 mt-4">
                <div className="eyebrow mb-3">{mod}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {items.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ft-${active.id}-${f.key}`}>
                      <input type="checkbox" checked={(active.features || []).includes(f.key) || (active.features || []).includes("*")} onChange={() => toggle(active, f.key)} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ SALESPERSON WORKFLOW CHECKLIST ============
export function WorkflowPage() {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    meeting_attended: false, training_attended: false,
    counter_sales: 0, follow_up_calls: 0, inventory_checked: false,
    scheme_started: 0, daily_target_amount: 0, target_achieved_amount: 0,
    target_miss_reason: "", notes: "",
  });
  const [items, setItems] = useState([]);
  const [targetInfo, setTargetInfo] = useState(null); // auto-fetched daily target

  const load = () => api.get("/api/crm/workflow/mine").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  // Today's target is assigned by the administrator this person reports to —
  // it is fetched automatically for the chosen date and cannot be typed.
  useEffect(() => {
    if (!form.date) return;
    api.get(`/api/crm/workflow/today-target?date=${form.date}`)
      .then((r) => {
        setTargetInfo(r.data);
        setForm((f) => ({ ...f, daily_target_amount: r.data?.daily_target || 0 }));
      })
      .catch(() => setTargetInfo(null));
    // eslint-disable-next-line
  }, [form.date]);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/api/crm/workflow", form);
    toast.success("Workflow logged");
    load();
  };

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-2 gap-6">
      <div>
        <div className="eyebrow">Daily</div>
        <h1 className="font-display text-3xl text-maroon">Workflow Checklist</h1>
        <form onSubmit={submit} className="bg-white border border-line p-5 mt-4 space-y-3">
          <DateInput required yearsBack={1} yearsAhead={1} value={form.date} onChange={(v) => setForm({...form, date: v})} data-testid="wf-date"/>
          {[
            ["meeting_attended", "Meeting attended", "bool"],
            ["training_attended", "Training attended", "bool"],
            ["inventory_checked", "Inventory checked today", "bool"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[k]} onChange={(e) => setForm({...form, [k]: e.target.checked})} data-testid={`wf-${k}`}/> {label}
            </label>
          ))}
          {/* Today's target — assigned by the person they report to, auto-fetched */}
          <div className="bg-gold/10 border border-gold/40 p-3" data-testid="wf-target-auto">
            <div className="flex items-center justify-between">
              <span className="eyebrow text-[10px]">Today's target (auto-fetched)</span>
              <b className="font-display text-xl text-maroon">₹{(form.daily_target_amount || 0).toLocaleString("en-IN")}</b>
            </div>
            <div className="text-[10px] text-ash mt-1">
              {targetInfo?.set_by
                ? <>Assigned by <b>{targetInfo.set_by}</b> · {targetInfo.note}{targetInfo.period ? ` (${targetInfo.period_type}: ${targetInfo.period})` : ""}</>
                : targetInfo?.note || "No target assigned yet — ask the administrator you report to."}
            </div>
          </div>
          {[
            ["counter_sales", "Counter sales (₹)"],
            ["follow_up_calls", "Follow-up calls made"],
            ["scheme_started", "Schemes started"],
            ["target_achieved_amount", "Achieved (₹)"],
          ].map(([k, label]) => (
            <label key={k} className="block text-sm">
              <span className="eyebrow text-[10px]">{label}</span>
              <input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form[k]} onChange={(e) => setForm({...form, [k]: +e.target.value})} data-testid={`wf-${k}`}/>
            </label>
          ))}
          <input placeholder="Reason for target miss (if any)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.target_miss_reason} onChange={(e) => setForm({...form, target_miss_reason: e.target.value})} data-testid="wf-miss"/>
          <textarea placeholder="Notes" rows={3} className="w-full border border-line px-3 py-2 bg-pearl" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <button className="btn-maroon w-full justify-center" data-testid="wf-submit">Save Workflow Log</button>
        </form>
      </div>
      <div>
        <div className="eyebrow mb-3">Recent days</div>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-line p-3" data-testid={`wf-row-${it.id}`}>
              <div className="flex justify-between items-center">
                <div className="font-medium">{it.date}</div>
                <div className="text-[10px] eyebrow">{it.completion_pct}% complete</div>
              </div>
              <div className="h-1 bg-line mt-1 overflow-hidden"><div className="h-full bg-maroon" style={{width: `${it.completion_pct}%`}}/></div>
              <div className="text-xs text-ash mt-2">Sales {fmtINR(it.counter_sales)} · Calls {it.follow_up_calls} · Schemes {it.scheme_started}</div>
              {it.target_miss_reason && <div className="text-xs text-maroon mt-1">Miss reason: {it.target_miss_reason}</div>}
            </div>
          ))}
          {items.length === 0 && <div className="text-center text-xs text-ash py-10">No workflow logs yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ SCHEME REMINDERS / AUTOMATION ============
export function SchemeRemindersPage() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [attachQr, setAttachQr] = useState(false);
  const [approvals, setApprovals] = useState([]);
  const load = () => api.get("/api/crm/schemes/reminders").then((r) => setItems(r.data || []));
  const loadApprovals = () => api.get("/api/crm/schemes/qr-approvals").then((r) => setApprovals(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadApprovals(); api.get("/api/crm/payment-qr").then((r) => setQrUrl(r.data?.qr_url || "")).catch(() => {}); }, []);

  const approve = async (a) => {
    try { await api.post(`/api/crm/schemes/${a.enrollment_id}/payments/${a.payment_id}/approve`); toast.success("Payment approved"); loadApprovals(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Optionally append the shop QR link to the reminder so the customer can pay.
  const linkWithQr = (waLink) => {
    if (!attachQr || !qrUrl) return waLink;
    const extra = encodeURIComponent(`\n\nPay via our QR: ${qrUrl}`);
    return waLink.includes("text=") ? `${waLink}${extra}` : waLink;
  };

  const runScan = async () => {
    setRunning(true);
    try { const { data } = await api.post("/api/crm/schemes/run-reminders"); toast.success(`Queued ${data.queued} new reminders (${data.scanned} enrollments scanned)`); load(); }
    catch { toast.error("Failed"); } finally { setRunning(false); }
  };

  const mark = async (id, status) => { await api.patch(`/api/crm/schemes/reminders/${id}`, { status }); load(); };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Automation</div><h1 className="font-display text-4xl text-maroon">Scheme Reminders</h1></div>
        <button onClick={runScan} disabled={running} className="btn-maroon" data-testid="run-scan-btn">{running ? "Scanning…" : "Run scan now"}</button>
      </div>
      <p className="text-xs text-ash mb-3">Detects pending installments, upcoming maturity (last month) and matured schemes. Each row gives you a personalised wa.me deeplink — tap, send, mark sent.</p>
      <label className="flex items-center gap-2 text-sm mb-4" data-testid="attach-qr-toggle">
        <input type="checkbox" checked={attachQr} onChange={(e) => setAttachQr(e.target.checked)} disabled={!qrUrl}/>
        Attach payment QR link to reminders {qrUrl ? "" : <span className="text-[10px] text-ash">(upload a QR in Billing Desk first)</span>}
      </label>

      {/* QR scheme-payment approvals */}
      {approvals.length > 0 && (
        <div className="bg-gold/10 border border-gold rounded-lg p-4 mb-6" data-testid="qr-approvals-panel">
          <div className="font-medium text-maroon mb-2">{approvals.length} QR scheme payment{approvals.length === 1 ? "" : "s"} awaiting approval</div>
          <div className="space-y-2">
            {approvals.map((a) => (
              <div key={`${a.enrollment_id}-${a.payment_id}`} className="flex flex-wrap items-center gap-2 bg-white border border-line p-2.5 rounded" data-testid={`approval-${a.payment_id}`}>
                <div className="flex-1 min-w-[180px] text-sm">
                  {a.customer_name} <span className="text-[10px] text-ash">· {a.scheme_name} · month {a.month_no}</span>
                  <div className="text-[10px] text-ash">{fmtINR(a.amount)} · marked by {a.marked_by}</div>
                </div>
                <button onClick={() => approve(a)} className="btn-maroon text-xs" data-testid={`approve-${a.payment_id}`}>Approve</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["Customer", "Phone", "Kind", "Status", "Queued for", "Action"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-line/60" data-testid={`rem-${r.id}`}>
                <td className="p-3">{r.customer_name}</td>
                <td className="p-3 text-ash">{r.customer_phone}</td>
                <td className="p-3"><span className="bg-gold/20 px-2 py-0.5 text-[10px] uppercase tracking-widest">{r.kind}</span></td>
                <td className="p-3">{r.status}</td>
                <td className="p-3 text-xs">{r.queued_for}</td>
                <td className="p-3 text-right">
                  {r.status === "queued" && (
                    <div className="flex gap-2 justify-end">
                      <a href={linkWithQr(r.wa_link)} target="_blank" rel="noreferrer" onClick={() => mark(r.id, "sent")} className="text-xs text-green-700 underline flex items-center gap-1" data-testid={`send-${r.id}`}><Send size={11}/> WhatsApp{attachQr && qrUrl ? " +QR" : ""}</a>
                      <button onClick={() => mark(r.id, "skipped")} className="text-xs text-ash underline" data-testid={`skip-${r.id}`}>Skip</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No reminders queued. Click "Run scan now".</div>}
      </div>
    </div>
  );
}

// ============ STORE-WISE INVENTORY ============
export function StockByStorePage() {
  const { user } = useAuth();
  const canTransfer = hasRole(user, ["super_admin", "major_admin", "admin"]);
  const [products, setProducts] = useState([]);
  const [stockMap, setStockMap] = useState({}); // {product_id: [{store, qty}]}
  const [stores, setStores] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [ledgerType, setLedgerType] = useState("added");
  const [ledger, setLedger] = useState([]);
  useEffect(() => {
    api.get(`/api/crm/inventory-ledger?type=${ledgerType}`).then((r) => setLedger(r.data || [])).catch(() => setLedger([]));
  }, [ledgerType]);

  // ---- Barcode-driven transfer builder ----
  const [xfer, setXfer] = useState({
    from_store: CANONICAL_STORES[0],
    to_store: CANONICAL_STORES[1] || CANONICAL_STORES[0],
    sent_through: "", note: "",
  });
  const [bc, setBc] = useState("");
  const [bcQty, setBcQty] = useState(1);
  const [found, setFound] = useState(null);      // {product, stock:[{store,qty}]}
  const [finding, setFinding] = useState(false);

  const load = async () => {
    const ps = (await api.get("/api/crm/products")).data || [];
    setProducts(ps);
    // Store VALUES must stay exactly as stored in the DB (stock rows key on this
    // string) — only the visible label is prettified via storeLabel().
    setStores([...CANONICAL_STORES]);
    const t = (await api.get("/api/crm/stock/transfers")).data || [];
    setTransfers(t);
  };
  useEffect(() => { load(); }, []);

  const pg = usePagination(products, 15);

  // Fetch stock for the rows currently on screen, skipping anything already held.
  useEffect(() => {
    const missing = pg.pageItems.filter((p) => !stockMap[p.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const gathered = {};
      for (const p of missing) {
        try {
          const { data } = await api.get(`/api/crm/stock/by-store/${p.id}`);
          gathered[p.id] = data;
        } catch { /* leave blank rather than break the table */ }
      }
      if (!cancelled && Object.keys(gathered).length) setStockMap((m) => ({ ...m, ...gathered }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pg.page, products]);

  const setStock = async (pid, store, qty) => {
    await api.post("/api/crm/stock/set", { product_id: pid, store, qty });
    toast.success("Stock saved");
    const { data } = await api.get(`/api/crm/stock/by-store/${pid}`);
    setStockMap({ ...stockMap, [pid]: data });
  };

  const availAtFrom = (stock) => (stock || []).find((s) => s.store === xfer.from_store)?.qty ?? 0;

  const findBarcode = async (e) => {
    e?.preventDefault?.();
    const code = bc.trim();
    if (!code) return;
    setFinding(true); setFound(null);
    try {
      const { data } = await api.get(`/api/crm/stock/lookup-barcode/${encodeURIComponent(code)}`);
      setFound(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No product for that barcode");
    } finally { setFinding(false); }
  };

  const commitTransfer = async () => {
    if (!found?.product) { toast.error("Find a product by barcode first"); return; }
    if (xfer.from_store === xfer.to_store) { toast.error("From and To stores must be different"); return; }
    if (!xfer.sent_through.trim()) { toast.error("Enter the person carrying the goods"); return; }
    const qty = Math.max(1, +bcQty || 1);
    if (availAtFrom(found.stock) < qty) { toast.error(`Only ${availAtFrom(found.stock)} in stock at ${storeLabel(xfer.from_store)}`); return; }
    try {
      await api.post("/api/crm/stock/transfer/bulk", {
        from_store: xfer.from_store, to_store: xfer.to_store,
        sent_through: xfer.sent_through, note: xfer.note,
        items: [{ product_id: found.product.id, qty }],
      });
      toast.success(`Transferred ${qty} × ${found.product.name}`);
      setFound(null); setBc(""); setBcQty(1);
      setXfer((x) => ({ ...x, sent_through: "", note: "" }));
      setStockMap({});   // force a refresh of the visible stock cells
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div>
        <div className="eyebrow">Multi-Store</div>
        <h1 className="font-display text-4xl text-maroon">Stock by Store</h1>
        <p className="text-xs text-ash mt-1">Tag-level qty per store. Edit cells directly, or move stock between stores by barcode below.</p>
      </div>

      {/* ---- Bulk CSV transfer ---- */}
      {canTransfer && <BulkCsvTransfer onDone={() => { setStockMap({}); load(); }} />}

      {/* ---- Barcode transfer builder ---- */}
      {canTransfer && (
        <div className="bg-white border border-line p-5" data-testid="transfer-builder">
          <div className="eyebrow mb-3">Transfer Stock Between Stores</div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <label className="block">
              <span className="text-[10px] text-ash uppercase tracking-widest">From store</span>
              <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={xfer.from_store} onChange={(e) => setXfer({ ...xfer, from_store: e.target.value })} data-testid="xfer-from">
                {stores.map((s) => <option key={s} value={s}>{storeLabel(s)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] text-ash uppercase tracking-widest">To store</span>
              <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={xfer.to_store} onChange={(e) => setXfer({ ...xfer, to_store: e.target.value })} data-testid="xfer-to">
                {stores.map((s) => <option key={s} value={s}>{storeLabel(s)}</option>)}
              </select>
            </label>
          </div>

          {/* Barcode lookup */}
          <form onSubmit={findBarcode} className="flex flex-wrap items-end gap-2">
            <label className="block flex-1 min-w-[180px]">
              <span className="text-[10px] text-ash uppercase tracking-widest">Barcode</span>
              <input autoFocus className="w-full border border-line px-3 py-2 bg-pearl mt-1 font-mono uppercase" placeholder="Scan or type barcode" value={bc} onChange={(e) => setBc(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase())} data-testid="xfer-barcode"/>
            </label>
            <button type="submit" disabled={finding || !bc.trim()} className="btn-outline text-sm disabled:opacity-50" data-testid="xfer-find">{finding ? "Finding…" : "Find"}</button>
          </form>

          {/* Found product → cover image + live per-store stock → qty → transfer */}
          {found?.product && (() => {
            const qty = Math.max(1, +bcQty || 1);
            const avail = availAtFrom(found.stock);
            const short = qty > avail;
            return (
              <div className="mt-3 border border-gold bg-gold/10 p-3" data-testid="xfer-found">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="w-20 h-20 bg-white border border-line overflow-hidden shrink-0">
                    {found.product.images?.[0]
                      ? <img src={found.product.images[0]} alt={found.product.name} className="w-full h-full object-cover"/>
                      : <div className="w-full h-full flex items-center justify-center text-[9px] text-ash">no image</div>}
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="font-medium">{found.product.name}</div>
                    <div className="text-[11px] text-ash">{found.product.category} · <span className="font-mono">{found.product.barcode}</span> · {found.product.metal === "Gemstone" ? `${found.product.carat_weight || 0}ct` : `${found.product.weight_grams || 0}g`}{found.product.purity && found.product.metal !== "Gemstone" ? ` · ${found.product.purity}` : ""}</div>
                    {/* Live stock per store */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {stores.map((s) => {
                        const q = (found.stock || []).find((x) => x.store === s)?.qty ?? 0;
                        return <span key={s} className={`text-[10px] px-2 py-0.5 rounded ${s === xfer.from_store ? "bg-maroon text-pearl" : "bg-white border border-line text-ash"}`}>{storeLabel(s)}: <b>{q}</b></span>;
                      })}
                    </div>
                  </div>
                  <label className="block">
                    <span className="text-[10px] text-ash uppercase tracking-widest">Items</span>
                    <input type="number" min={1} className="w-20 border border-line px-2 py-1.5 bg-white mt-1 block" value={bcQty} onChange={(e) => setBcQty(+e.target.value)} data-testid="xfer-qty"/>
                  </label>
                </div>
                {short && (
                  <div className="mt-3 bg-red-50 border border-red-300 text-red-700 text-xs p-2 rounded" data-testid="xfer-short">
                    Only {avail} in stock at {storeLabel(xfer.from_store)} — cannot transfer {qty}. Reduce to {avail}, or add more stock at {storeLabel(xfer.from_store)} first.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Carrier + commit */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <input placeholder="Sent through (person carrying the goods)" className="border border-line px-3 py-2 bg-pearl" value={xfer.sent_through} onChange={(e) => setXfer({ ...xfer, sent_through: e.target.value })} data-testid="xfer-sent-through"/>
            <input placeholder="Note (optional)" className="border border-line px-3 py-2 bg-pearl" value={xfer.note} onChange={(e) => setXfer({ ...xfer, note: e.target.value })}/>
          </div>
          <button onClick={commitTransfer} disabled={!found?.product} className="btn-maroon w-full justify-center mt-3 disabled:opacity-50" data-testid="xfer-commit">
            Transfer from {storeLabel(xfer.from_store)} → {storeLabel(xfer.to_store)}
          </button>
        </div>
      )}

      <div className="bg-white border border-line">
        <Pagination {...pg} label="products" compact/>
        <TableScroller>
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>
              <th className="text-left p-3 eyebrow text-[10px]">Product</th>
              {stores.map((s) => <th key={s} className="text-left p-3 eyebrow text-[10px]">{storeLabel(s)}</th>)}
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((p) => (
              <tr key={p.id} className="border-b border-line/60" data-testid={`stock-row-${p.id}`}>
                <td className="p-3"><div className="font-medium">{p.name}</div><div className="text-[10px] text-ash">{p.category} · {p.barcode || p.huid || "no barcode"}</div></td>
                {stores.map((s) => {
                  const entry = (stockMap[p.id] || []).find((x) => x.store === s);
                  const qty = entry?.qty ?? 0;
                  return (
                    <td key={s} className="p-2">
                      <input type="number" defaultValue={qty} key={`${p.id}-${s}-${qty}`} onBlur={(e) => +e.target.value !== qty && setStock(p.id, s, +e.target.value)} className="w-16 border border-line px-2 py-1 bg-pearl text-sm" data-testid={`stock-${p.id}-${s}`}/>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
          </TableScroller>
        <Pagination {...pg} label="products"/>
      </div>

      <div>
        <div className="eyebrow mb-3">Transfer History</div>
        <div className="bg-white border border-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line"><tr>{["When", "Product", "From", "To", "Qty", "Sent Through", "By", "Status"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id} className="border-b border-line/60" data-testid={`xfer-${t.id}`}>
                  <td className="p-3 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs font-mono">{t.product_id?.slice(0,8)}</td>
                  <td className="p-3 text-xs">{storeLabel(t.from_store)}</td>
                  <td className="p-3 text-xs">{storeLabel(t.to_store)}</td>
                  <td className="p-3">{t.qty}</td>
                  <td className="p-3 text-xs">{t.sent_through || "—"}</td>
                  <td className="p-3 text-xs">{t.requested_by_name}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${t.status === "approved" ? "bg-green-100 text-green-800" : t.status === "rejected" ? "bg-red-100 text-red-700" : "bg-gold/25 text-maroon"}`}>{t.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {transfers.length === 0 && <div className="p-8 text-center text-ash text-sm">No transfers yet.</div>}
        </div>
      </div>

      {/* Inventory Ledger — Added vs Updated */}
      <div>
        <div className="eyebrow mb-3">Inventory Ledger</div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setLedgerType("added")} className={`px-4 py-2 text-xs rounded-full border ${ledgerType === "added" ? "bg-maroon text-pearl border-maroon" : "bg-white border-line text-ink"}`} data-testid="ledger-added">Inventory Added</button>
          <button onClick={() => setLedgerType("updated")} className={`px-4 py-2 text-xs rounded-full border ${ledgerType === "updated" ? "bg-maroon text-pearl border-maroon" : "bg-white border-line text-ink"}`} data-testid="ledger-updated">Updated Inventory</button>
        </div>
        <div className="bg-white border border-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line"><tr>{["When", "Product", "Store", ledgerType === "added" ? "Added" : "Set to", "By"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
            <tbody>
              {ledger.map((l) => (
                <tr key={l.id} className="border-b border-line/60" data-testid={`ledger-row-${l.id}`}>
                  <td className="p-3 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="p-3 text-xs">{l.product_name || l.product_id?.slice(0, 8)}<div className="text-[9px] text-ash font-mono">{l.barcode || ""}</div></td>
                  <td className="p-3 text-xs">{storeLabel(l.store)}</td>
                  <td className="p-3">{ledgerType === "added" ? `+${l.qty}` : l.qty}{ledgerType === "updated" && typeof l.delta === "number" ? <span className="text-[10px] text-ash"> ({l.delta >= 0 ? "+" : ""}{l.delta})</span> : null}</td>
                  <td className="p-3 text-xs">{l.by}{l.role ? <span className="text-ash"> ({(l.role || "").replace("_", " ")})</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ledger.length === 0 && <div className="p-8 text-center text-ash text-sm">No {ledgerType === "added" ? "stock additions" : "stock edits"} yet.</div>}
        </div>
      </div>
    </div>
  );
}
// ============ BULK CSV STOCK TRANSFER ============
function parseCSV(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return lines.slice(1).map((line, i) => {
    const cells = line.split(",");
    const row = { row: i + 2 };
    headers.forEach((h, idx) => { row[h] = (cells[idx] || "").trim(); });
    return row;
  });
}

function BulkCsvTransfer({ onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null); // { total, done, failed, results }

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setReport(null);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length === 0) { toast.error("CSV looks empty or has no data rows"); setBusy(false); return; }
      const { data } = await api.post("/api/crm/stock/transfer/csv", { rows });
      setReport(data);
      toast.success(`${data.done} of ${data.total} rows transferred`);
      if (data.done > 0) onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = () => {
    const csv = "barcode,qty,from_store,to_store,sent_through\nSG0001,2,Swayambhu Gold,Shri Mahaganapati Jewellers,Ramesh\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "stock-transfer-template.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  };

  const failed = (report?.results || []).filter((r) => r.status === "failed");

  return (
    <div className="bg-white border border-line p-5" data-testid="bulk-csv-transfer">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="eyebrow">Bulk transfer via CSV</div>
        <button onClick={downloadTemplate} className="text-xs text-maroon underline" data-testid="csv-template">Download template</button>
      </div>
      <p className="text-xs text-ash mb-3">Upload a CSV with columns <b>barcode, qty, from_store, to_store</b> (sent_through optional). Each row is transferred independently — good rows move, and any that fail are listed below to redo manually.</p>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}/>
      <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-maroon disabled:opacity-50" data-testid="csv-upload">
        <Upload size={14}/> {busy ? "Processing…" : "Upload CSV"}
      </button>

      {report && (
        <div className="mt-4" data-testid="csv-report">
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="bg-pearl border border-line px-3 py-2 text-sm"><b>{report.total}</b> rows</div>
            <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm"><b>{report.done}</b> transferred</div>
            <div className={`px-3 py-2 text-sm border ${report.failed ? "bg-red-50 border-red-200 text-red-700" : "bg-pearl border-line"}`}><b>{report.failed}</b> failed</div>
          </div>
          {failed.length > 0 && (
            <Paginated items={failed} pageSize={10}>
              {(pageItems) => (
                <div className="border border-line overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-pearl border-b border-line"><tr>{["Row", "Barcode", "Qty", "From → To", "Reason"].map((h) => <th key={h} className="text-left p-2 eyebrow text-[10px]">{h}</th>)}</tr></thead>
                    <tbody>
                      {pageItems.map((r, i) => (
                        <tr key={i} className="border-b border-line/60 bg-red-50/40" data-testid={`csv-failed-${r.row}`}>
                          <td className="p-2">{r.row}</td>
                          <td className="p-2 font-mono">{r.barcode || "—"}</td>
                          <td className="p-2">{r.qty}</td>
                          <td className="p-2">{r.from_store || "—"} → {r.to_store || "—"}</td>
                          <td className="p-2 text-red-700">{r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Paginated>
          )}
        </div>
      )}
    </div>
  );
}

// ============ EOD WHATSAPP CLICK-PACK ============
export function ClickPackPage() {
  const [data, setData] = useState(null);
  const [done, setDone] = useState(new Set());
  useEffect(() => { api.get("/api/crm/eod/click-pack").then((r) => setData(r.data)); }, []);
  const fire = (item, i) => {
    window.open(item.wa_link, "_blank");
    setDone(new Set([...done, i]));
    if (item.reminder_id) api.post("/api/crm/eod/click-pack/mark-sent", { reminder_id: item.reminder_id });
  };
  if (!data) return <div className="p-10">Loading…</div>;
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Tap-tap-tap</div>
        <h1 className="font-display text-4xl text-maroon">EOD WhatsApp Click-Pack</h1>
        <p className="text-xs text-ash mt-1">{data.total} messages queued for today: birthdays, anniversaries, HOT predictive followups and scheme reminders. Tap each in sequence — wa.me opens with the personalised text ready to send.</p>
      </div>
      <div className="space-y-2 max-w-3xl">
        {data.items.map((it, i) => (
          <div key={i} className={`bg-white border ${done.has(i) ? "border-green-700" : "border-line"} p-4 flex items-center gap-3`} data-testid={`cp-item-${i}`}>
            <div className="w-8 h-8 rounded-full bg-pearl flex items-center justify-center font-semibold text-maroon text-sm">{i + 1}</div>
            <div className="flex-1">
              <div className="font-medium">{it.name} <span className="text-[10px] text-ash uppercase tracking-widest ml-2">{it.kind.replace("_", " ")}</span></div>
              <div className="text-xs text-ash">{it.phone}{it.ltv && ` · LTV ${fmtINR(it.ltv)}`}</div>
            </div>
            {done.has(i) ? <CheckCircle size={18} className="text-green-700"/> : (
              <button onClick={() => fire(it, i)} className="btn-gold text-xs flex items-center gap-1" data-testid={`cp-fire-${i}`}><MessageCircle size={12}/> Send</button>
            )}
          </div>
        ))}
        {data.items.length === 0 && <div className="text-center text-ash py-10">All caught up — nothing pending for today.</div>}
      </div>
    </div>
  );
}

// ============ FESTIVAL CANONICAL ENGINE ============
export function FestivalEnginePage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [fForm, setFForm] = useState({ name: "", date: "" });
  const { user } = useAuth();
  const canManage = hasRole(user, ["super_admin", "major_admin", "admin"]);

  const loadEngine = () => api.get(`/api/crm/festival/canonical?days=${days}`).then((r) => setData(r.data));
  const loadFestivals = () => api.get("/api/crm/festivals").then((r) => setFestivals(r.data || []));
  useEffect(() => {
    loadEngine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  useEffect(() => { loadFestivals(); }, []);

  const addFestival = async (e) => {
    e.preventDefault();
    if (!fForm.name.trim() || !fForm.date) { toast.error("Festival name and date required"); return; }
    try {
      await api.post("/api/crm/festivals", fForm);
      toast.success("Festival added");
      setFForm({ name: "", date: "" });
      loadFestivals(); loadEngine();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeFestival = async (f) => {
    if (!window.confirm(`Remove "${f.name}"?`)) return;
    await api.delete(`/api/crm/festivals/${f.id}`);
    toast.success("Removed");
    loadFestivals(); loadEngine();
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Engine</div><h1 className="font-display text-4xl text-maroon">Festival Date Engine</h1></div>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={days} onChange={(e) => setDays(+e.target.value)} data-testid="fe-window">
          <option value={1}>Today</option><option value={3}>Next 3 days</option><option value={7}>Next 7 days</option><option value={30}>Next 30 days</option>
        </select>
      </div>

      {/* Admin-managed festival dates — nothing is predefined */}
      {canManage && (
        <div className="bg-white border border-line p-5 mb-6">
          <div className="eyebrow mb-3">Manage Festival Dates</div>
          <form onSubmit={addFestival} className="flex flex-wrap gap-2 mb-3">
            <input placeholder="Festival name (e.g. Diwali)" className="border border-line px-3 py-2 bg-pearl text-sm flex-1 min-w-[180px]" value={fForm.name} onChange={(e) => setFForm({...fForm, name: e.target.value})} data-testid="festival-name-input"/>
            <DateInput yearsBack={0} yearsAhead={2} value={fForm.date} onChange={(v) => setFForm({...fForm, date: v})} data-testid="festival-date-input"/>
            <button className="btn-maroon text-sm" data-testid="add-festival-btn">Add</button>
          </form>
          <div className="flex flex-wrap gap-2">
            {festivals.map((f) => (
              <div key={f.id} className="inline-flex items-center gap-2 border border-line rounded-full px-3 py-1 text-sm" data-testid={`festival-chip-${f.id}`}>
                <span>{f.name}</span>
                <span className="text-xs text-ash">{f.date}</span>
                <button onClick={() => removeFestival(f)} className="text-ash hover:text-red-600" data-testid={`remove-festival-${f.id}`}><X size={12}/></button>
              </div>
            ))}
            {festivals.length === 0 && <span className="text-xs text-ash">No festivals added yet — add the dates your store celebrates.</span>}
          </div>
        </div>
      )}

      {data && (
        <div className="grid lg:grid-cols-3 gap-6">
          <section className="bg-white border border-line p-5">
            <div className="eyebrow mb-3">Upcoming Festivals</div>
            {data.upcoming_festivals?.map((f) => (
              <div key={f.id || f.name} className="py-2 border-b border-line/60" data-testid={`fest-${f.name}`}>
                <div className="font-medium">{f.name}</div>
                <div className="text-xs text-ash">{f.date}</div>
              </div>
            ))}
            {(!data.upcoming_festivals || data.upcoming_festivals.length === 0) && <div className="text-xs text-ash">No upcoming festivals — add them above.</div>}
          </section>
          <section className="bg-white border border-line p-5">
            <div className="eyebrow mb-3">Birthdays in window ({data.birthdays?.length})</div>
            {data.birthdays?.map((b) => (
              <div key={b.customer.id} className="py-2 border-b border-line/60" data-testid={`fe-b-${b.customer.id}`}>
                <div className="font-medium">{b.customer.name}</div>
                <div className="text-xs text-ash">In {b.days_away}d · {b.customer.phone}</div>
              </div>
            ))}
          </section>
          <section className="bg-white border border-line p-5">
            <div className="eyebrow mb-3">Anniversaries in window ({data.anniversaries?.length})</div>
            {data.anniversaries?.map((b) => (
              <div key={b.customer.id} className="py-2 border-b border-line/60" data-testid={`fe-a-${b.customer.id}`}>
                <div className="font-medium">{b.customer.name}</div>
                <div className="text-xs text-ash">In {b.days_away}d · {b.customer.phone}</div>
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
